import React, { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Languages } from "lucide-react";
import Logo from "@/components/Logo";
import { useLanguage } from "@/context/LanguageContext";

const NAV = [
  { to: "/", key: "nav.home" },
  { to: "/hakkimizda", key: "nav.about" },
  { to: "/hizmetler", key: "nav.services" },
  { to: "/blog", key: "nav.blog" },
  { to: "/iletisim", key: "nav.contact" },
];

const LanguageToggle = ({ className = "" }) => {
  const { language, toggleLanguage, t } = useLanguage();
  return (
    <button
      type="button"
      onClick={toggleLanguage}
      aria-label={t("header.langSwitchLabel")}
      title={t("header.langSwitchLabel")}
      className={`inline-flex items-center gap-1.5 px-3 h-9 rounded-full border border-slate-200 text-xs font-semibold tracking-wide text-slate-700 hover:border-teal-700 hover:text-teal-700 transition-colors ${className}`}
      data-testid="language-toggle"
    >
      <Languages size={13} />
      {language === "tr" ? "EN" : "TR"}
    </button>
  );
};

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { t } = useLanguage();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      className={`sticky top-0 z-40 transition-all ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-slate-200/80"
          : "bg-white/60 backdrop-blur-md border-b border-transparent"
      }`}
      data-testid="site-header"
    >
      {/* Top utility bar with language toggle */}
      <div className="border-b border-slate-100/80 bg-slate-50/70">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-9 flex items-center justify-end">
          <LanguageToggle />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between">
        <Link to="/" className="shrink-0" data-testid="header-logo-link">
          <Logo size={36} />
        </Link>

        <nav className="hidden lg:flex items-center gap-9" data-testid="primary-nav">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              className={({ isActive }) =>
                `text-[14px] font-medium tracking-wide transition-colors link-underline ${
                  isActive ? "text-teal-700" : "text-slate-700 hover:text-teal-700"
                }`
              }
              data-testid={`nav-link-${n.key.split(".")[1]}`}
            >
              {t(n.key)}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href="tel:+902128120295"
            className="inline-flex items-center gap-2 text-sm text-slate-700 hover:text-teal-700"
            data-testid="header-phone-link"
          >
            <Phone size={15} />
            <span className="font-medium">0212 812 02 95</span>
          </a>
          <Link
            to="/iletisim"
            className="inline-flex items-center gap-2 px-5 h-10 rounded-full bg-teal-700 text-white text-sm font-medium hover:bg-teal-800 transition-colors"
            data-testid="header-cta-quote"
          >
            {t("header.cta")}
          </Link>
        </div>

        <div className="lg:hidden flex items-center gap-2">
          <button
            className="inline-flex items-center justify-center h-10 w-10 rounded-full border border-slate-200 text-slate-700"
            onClick={() => setOpen((v) => !v)}
            aria-label={t("header.menuToggle")}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-100 bg-white" data-testid="mobile-nav">
          <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                className={({ isActive }) =>
                  `py-3 px-2 rounded-md text-base ${
                    isActive ? "text-teal-700 bg-teal-50" : "text-slate-800 hover:bg-slate-50"
                  }`
                }
                data-testid={`mobile-nav-link-${n.key.split(".")[1]}`}
              >
                {t(n.key)}
              </NavLink>
            ))}
            <Link
              to="/iletisim"
              className="mt-2 inline-flex items-center justify-center gap-2 px-5 h-11 rounded-full bg-teal-700 text-white text-sm font-medium"
              data-testid="mobile-cta-quote"
            >
              {t("header.cta")}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
